import * as THREE from "three";
import {
  DEFAULT_FIXING_ID,
  FINISH,
  getFixingSpec,
  getPreset,
  SCULPT,
  SURFACE_POLISH,
  type BlockPreset,
  type BlockPresetId,
} from "@/config/designer";
import { buildStrokeDabs, unionRegion, type BrushKind, type SampleRegion } from "@/core/field/brushes";
import { VoxelField } from "@/core/field/VoxelField";
import { applyFlange as applyFieldFlange, type FlangeResult } from "@/core/field/flange";
import {
  removeFloatingGeometry as removeFloatingFieldGeometry,
  type RemoveFloatingResult,
} from "@/core/field/floating";
import {
  applyOverallSmooth,
  type OverallSmoothResult,
} from "@/core/field/overallSmooth";
import { buildFixingCutter, checkPlacement, type PlacementCheck } from "@/core/fixings/holeSolid";
import { createFixingId, type Fixing } from "@/core/fixings/types";
import { HistoryStack, type HistoryCommand } from "@/core/history/HistoryStack";
import { StrokeRecorder } from "@/core/history/StrokeRecorder";
import { applyCut, type CutResult, type CutSweep } from "@/core/cut/applyCut";
import { relaxSurfaceBand, regularizeAll, regularizeRegion } from "@/core/field/regularize";
import { ChunkedMesher } from "@/core/mesh/ChunkedMesher";
import { computeFieldAO, expandAOToColors } from "@/core/mesh/fieldAO";
import { claySmoothMesh, computeCreasedNormals } from "@/core/mesh/meshUtils";
import { extractSurfaceNets } from "@/core/mesh/surfaceNets";
import { subtractSolids } from "@/core/export/csg";
import type { SavedDesign } from "@/core/persistence/storage";
import type { GlossDab } from "@/core/finish/types";

export type EngineEvent = "geometry" | "fixings" | "finish" | "history" | "solid";

export interface SurfaceHit {
  point: [number, number, number];
  normal: [number, number, number];
}

/** Segment count for the viewport preview — the export uses the quality setting. */
const PREVIEW_SEGMENTS = 40;

interface GeometrySnapshot {
  field: Float32Array;
  dirty: Uint8Array;
  fixings: Fixing[];
  glossDabs: GlossDab[];
}

/**
 * Owns every piece of mutable design state: the voxel field, the display
 * meshes, the fixing list and the undo stack. Deliberately plain TypeScript
 * with an event emitter rather than React state — a 5 MB Float32Array has no
 * business going through a reducer, and the render loop needs to read it
 * without re-rendering the tree.
 */
export class DesignerEngine {
  readonly preset: BlockPreset;
  readonly field: VoxelField;
  readonly history = new HistoryStack();
  readonly mesher: ChunkedMesher;
  readonly group = new THREE.Group();

  private fixingList: Fixing[] = [];
  private glossList: GlossDab[] = [];
  private glossStrokeBefore: GlossDab[] | null = null;
  private lastGlossPoint: GlossDab | null = null;
  private overallSmoothPreview: GeometrySnapshot | null = null;
  private flangePreview: GeometrySnapshot | null = null;
  private readonly listeners = new Map<EngineEvent, Set<() => void>>();

  private stroke: StrokeRecorder | null = null;
  private strokeRegion: SampleRegion | null = null;
  private lastPoint: { x: number; y: number; z: number; topness: number } | null = null;

  /** Single mesh with the fixing holes actually cut, shown when holes exist. */
  private solidMesh: THREE.Mesh | null = null;
  private solidToken = 0;
  private solidPending = false;
  private solidStale = true;

  constructor(
    presetId: BlockPresetId,
    private material: THREE.Material,
  ) {
    this.preset = getPreset(presetId);
    this.field = new VoxelField(
      this.preset.width,
      this.preset.height,
      this.preset.depth,
      this.preset.voxelSize,
    );
    this.mesher = new ChunkedMesher(this.field, material);
    this.group.add(this.mesher.group);
    this.mesher.flush();
  }

  // ---------------------------------------------------------------- events

  on(event: EngineEvent, listener: () => void): () => void {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(listener);
    return () => set.delete(listener);
  }

  private emit(event: EngineEvent): void {
    const set = this.listeners.get(event);
    if (!set) return;
    for (const listener of set) listener();
  }

  // ------------------------------------------------------------- geometry

  get fixings(): readonly Fixing[] {
    return this.fixingList;
  }

  get glossDabs(): readonly GlossDab[] {
    return this.glossList;
  }

  get isSculpting(): boolean {
    return this.stroke !== null;
  }

  get solidBusy(): boolean {
    return this.solidPending;
  }

  /** Triangles in the holes-cut viewport mesh, or 0 when it is not in use. */
  get solidTriangleCount(): number {
    const attribute = this.solidMesh?.geometry.getAttribute("position");
    return this.solidMesh?.visible && attribute ? attribute.count / 3 : 0;
  }

  setMaterial(material: THREE.Material): void {
    this.material = material;
    this.mesher.setMaterial(material);
    if (this.solidMesh) this.solidMesh.material = material;
  }

  /** Default brush radius for this block, mm. */
  get defaultBrushRadius(): number {
    const span = Math.max(this.preset.width, this.preset.depth);
    return clamp(span * SCULPT.defaultRadiusFraction, SCULPT.minRadius, SCULPT.maxRadius);
  }

  raycast(origin: THREE.Vector3, direction: THREE.Vector3): SurfaceHit | null {
    return this.field.raycast(
      [origin.x, origin.y, origin.z],
      [direction.x, direction.y, direction.z],
    );
  }

  // -------------------------------------------------------------- sculpt

  beginStroke(): void {
    this.stroke = new StrokeRecorder(this.field);
    this.strokeRegion = null;
    this.lastPoint = null;
    this.setSolidVisible(false);
  }

  /**
   * Continue the current stroke to a surface point. Returns true if anything
   * changed, so the caller can skip a needless re-render.
   */
  strokeTo(
    hit: SurfaceHit,
    kind: BrushKind,
    radius: number,
    strength: number,
    lockedBaseMm = 0,
  ): boolean {
    const recorder = this.stroke;
    if (!recorder) return false;

    const topness = clamp(hit.normal[1], 0, 1);
    const to = { x: hit.point[0], y: hit.point[1], z: hit.point[2], topness };
    const amount =
      kind === "carve" ? strength * SCULPT.strengthToMillimetres : clamp(strength, 0, 1);

    const dabs = buildStrokeDabs(this.lastPoint, to, kind, radius, amount, lockedBaseMm);
    this.lastPoint = to;

    let changed: SampleRegion | null = null;
    for (const dab of dabs) {
      changed = unionRegion(changed, recorder.apply(dab));
    }
    if (!changed) return false;

    recorder.regularize(changed);
    this.strokeRegion = unionRegion(this.strokeRegion, changed);
    this.mesher.markRegionDirty(changed);
    // Spend at most half a frame here; anything left over is finished by the
    // render loop, so a fast drag lags by a frame rather than stuttering.
    this.mesher.flush(8);
    this.emit("geometry");
    return true;
  }

  endStroke(kind: BrushKind): void {
    const recorder = this.stroke;
    this.stroke = null;
    this.lastPoint = null;
    this.strokeRegion = null;
    if (!recorder) return;

    const command = recorder.toCommand(kind === "carve" ? "Carve" : "Smooth", (region) => {
      this.mesher.markRegionDirty(region);
      this.mesher.flush();
      this.invalidateSolid();
      this.emit("geometry");
    });
    if (command) this.history.push(command);
    this.invalidateSolid();
    this.emit("history");
  }

  // -------------------------------------------------------- finish paint

  beginGlossStroke(): void {
    this.glossStrokeBefore = [...this.glossList];
    this.lastGlossPoint = null;
  }

  paintGloss(hit: SurfaceHit, radius: number): boolean {
    if (!this.glossStrokeBefore || this.glossList.length >= FINISH.maxGlossDabs) return false;
    const next: GlossDab = {
      x: hit.point[0],
      y: hit.point[1],
      z: hit.point[2],
      radius,
    };
    const previous = this.lastGlossPoint;
    if (
      previous &&
      Math.hypot(next.x - previous.x, next.y - previous.y, next.z - previous.z) <
        radius * FINISH.dabSpacingFraction
    ) {
      return false;
    }
    this.glossList = [...this.glossList, next];
    this.lastGlossPoint = next;
    this.emit("finish");
    return true;
  }

  endGlossStroke(): void {
    const before = this.glossStrokeBefore;
    this.glossStrokeBefore = null;
    this.lastGlossPoint = null;
    if (!before || before.length === this.glossList.length) return;
    const after = [...this.glossList];
    this.history.push({
      label: "Paint gloss",
      bytes: (before.length + after.length) * 32,
      undo: () => this.applyGlossPaint(before),
      redo: () => this.applyGlossPaint(after),
    });
    this.emit("history");
  }

  clearGlossPaint(): void {
    if (this.glossList.length === 0) return;
    const before = [...this.glossList];
    this.applyGlossPaint([]);
    this.history.push({
      label: "Clear gloss paint",
      bytes: before.length * 32,
      undo: () => this.applyGlossPaint(before),
      redo: () => this.applyGlossPaint([]),
    });
    this.emit("history");
  }

  private applyGlossPaint(next: readonly GlossDab[]): void {
    this.glossList = [...next];
    this.emit("finish");
  }

  // ------------------------------------------------------------- fixings

  checkFixingPlacement(x: number, z: number, typeId = DEFAULT_FIXING_ID): PlacementCheck {
    return checkPlacement(this.field, getFixingSpec(typeId), x, z);
  }

  addFixing(x: number, z: number, typeId = DEFAULT_FIXING_ID): Fixing | null {
    if (!this.checkFixingPlacement(x, z, typeId).valid) return null;
    const fixing: Fixing = { id: createFixingId(), typeId, x, z };
    const before = this.fixingList;
    const after = [...before, fixing];
    this.applyFixings(after);
    this.history.push(this.fixingCommand("Add fixing", before, after));
    this.emit("history");
    return fixing;
  }

  moveFixing(id: string, x: number, z: number): void {
    const before = this.fixingList;
    const existing = before.find((f) => f.id === id);
    if (!existing) return;
    if (!this.checkFixingPlacement(x, z, existing.typeId).valid) return;
    const after = before.map((f) => (f.id === id ? { ...f, x, z } : f));
    this.applyFixings(after);
    this.history.push(this.fixingCommand("Move fixing", before, after));
    this.emit("history");
  }

  /** Live drag: updates geometry without pushing a history entry. */
  previewMoveFixing(id: string, x: number, z: number): void {
    const existing = this.fixingList.find((f) => f.id === id);
    if (!existing) return;
    if (!this.checkFixingPlacement(x, z, existing.typeId).valid) return;
    this.fixingList = this.fixingList.map((f) => (f.id === id ? { ...f, x, z } : f));
    this.invalidateSolid();
    this.emit("fixings");
  }

  removeFixing(id: string): void {
    const before = this.fixingList;
    const after = before.filter((f) => f.id !== id);
    if (after.length === before.length) return;
    this.applyFixings(after);
    this.history.push(this.fixingCommand("Delete fixing", before, after));
    this.emit("history");
  }

  private fixingCommand(
    label: string,
    before: readonly Fixing[],
    after: readonly Fixing[],
  ): HistoryCommand {
    return {
      label,
      bytes: (before.length + after.length) * 64,
      undo: () => this.applyFixings([...before]),
      redo: () => this.applyFixings([...after]),
    };
  }

  private applyFixings(next: Fixing[]): void {
    this.fixingList = next;
    this.invalidateSolid();
    this.emit("fixings");
  }

  /** Nearest fixing to a footprint point, within `radius` mm. */
  fixingNear(x: number, z: number, radius: number): Fixing | null {
    let best: Fixing | null = null;
    let bestDistance = radius;
    for (const fixing of this.fixingList) {
      const d = Math.hypot(fixing.x - x, fixing.z - z);
      if (d <= bestDistance) {
        bestDistance = d;
        best = fixing;
      }
    }
    return best;
  }

  // --------------------------------------------------- holes in the view

  private setSolidVisible(visible: boolean): void {
    if (this.solidMesh) this.solidMesh.visible = visible;
    this.mesher.group.visible = !visible || !this.solidMesh;
  }

  private invalidateSolid(): void {
    this.solidStale = true;
    this.solidToken++;
    if (this.fixingList.length === 0) {
      this.disposeSolid();
      this.setSolidVisible(false);
    }
  }

  private disposeSolid(): void {
    if (!this.solidMesh) return;
    this.group.remove(this.solidMesh);
    this.solidMesh.geometry.dispose();
    this.solidMesh = null;
    this.mesher.group.visible = true;
  }

  /**
   * Rebuild the viewport mesh with the fixing holes genuinely cut out, so what
   * the user inspects is what the STL contains. Debounced by the caller and
   * skipped entirely while a stroke is in flight.
   */
  async refreshSolid(): Promise<void> {
    if (this.stroke) return;
    if (this.fixingList.length === 0 || !this.solidStale) return;
    if (this.solidPending) return;

    this.solidPending = true;
    const token = ++this.solidToken;
    this.emit("solid");
    try {
      const base = claySmoothMesh(
        extractSurfaceNets(this.field),
        this.field,
        SURFACE_POLISH.fixingPreviewIterations,
      );
      const cutters = this.fixingList
        .map((f) => buildFixingCutter(this.field, f, PREVIEW_SEGMENTS))
        .filter((c): c is NonNullable<typeof c> => c !== null);
      const result = await subtractSolids(base, cutters);
      if (token !== this.solidToken) return;
      if (!result.ok || !result.mesh) {
        // Preview only — the export path has its own fallback.
        this.setSolidVisible(false);
        return;
      }
      const shaded = computeCreasedNormals(result.mesh);
      if (!this.solidMesh) {
        this.solidMesh = new THREE.Mesh(new THREE.BufferGeometry(), this.material);
        this.solidMesh.castShadow = true;
        this.solidMesh.receiveShadow = true;
        this.solidMesh.raycast = () => undefined;
        this.group.add(this.solidMesh);
      }
      const geometry = this.solidMesh.geometry;
      const colors = expandAOToColors(
        computeFieldAO(this.field, result.mesh.positions, 0.18),
        result.mesh.indices,
        result.mesh.indices.length / 3,
      );
      geometry.setAttribute("position", new THREE.BufferAttribute(shaded.positions, 3));
      geometry.setAttribute("normal", new THREE.BufferAttribute(shaded.normals, 3));
      geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
      geometry.computeBoundingSphere();
      this.solidStale = false;
      this.setSolidVisible(true);
      this.emit("geometry");
    } finally {
      this.solidPending = false;
      this.emit("solid");
    }
  }

  // ----------------------------------------------------------------- cut

  /**
   * Saw straight through the foam along a drawn line and drop the offcut.
   *
   * The whole field can move, so the undo entry keeps a full snapshot of the
   * "before" state and redo re-runs the cut from it — the cut is deterministic
   * given the same sweep, so replaying costs nothing but a few milliseconds and
   * halves what the history has to hold.
   */
  cut(sweep: CutSweep): CutResult {
    const beforeField = new Float32Array(this.field.data);
    const beforeDirty = new Uint8Array(this.field.dirty);
    const beforeFixings = this.fixingList;

    const result = applyCut(this.field, sweep);
    if (!result.applied) {
      // The kerf pass may already have written; put it back exactly.
      this.field.data.set(beforeField);
      this.field.dirty.set(beforeDirty);
      return result;
    }

    const settle = (region: SampleRegion | null): void => {
      if (region) {
        regularizeRegionAround(this.field, region);
        // Cuts can replace a long side and its whole mounting-plane outline in
        // one operation. Give that fresh surface a stronger clay pass before
        // the contour-aware mesh polish runs.
        relaxSurfaceBand(this.field, region, 8, 0.68);
        this.mesher.markRegionDirty(expandSampleRegion(this.field, region, 4));
      } else {
        regularizeAll(this.field);
        relaxSurfaceBand(this.field, null, 8, 0.68);
        this.mesher.markAllDirty();
      }
      this.mesher.flush();
      this.invalidateSolid();
      this.emit("geometry");
      this.emit("fixings");
    };

    this.fixingList = this.fixingList.filter(
      (fixing) => this.checkFixingPlacement(fixing.x, fixing.z, fixing.typeId).valid,
    );
    settle(result.region);

    const restore = (): void => {
      this.field.data.set(beforeField);
      this.field.dirty.set(beforeDirty);
      this.fixingList = [...beforeFixings];
      this.mesher.markAllDirty();
      this.mesher.flush();
      this.invalidateSolid();
      this.emit("geometry");
      this.emit("fixings");
    };

    this.history.push({
      label: "Cut",
      bytes: beforeField.byteLength + beforeDirty.byteLength,
      undo: restore,
      redo: () => {
        restore();
        const again = applyCut(this.field, sweep);
        this.fixingList = this.fixingList.filter(
          (fixing) => this.checkFixingPlacement(fixing.x, fixing.z, fixing.typeId).valid,
        );
        settle(again.region);
      },
    });
    this.emit("history");
    return result;
  }

  // -------------------------------------------------------------- flange

  beginFlangePreview(): void {
    if (this.overallSmoothPreview) this.cancelOverallSmoothPreview();
    if (this.flangePreview) this.cancelFlangePreview();
    this.flangePreview = this.captureGeometry();
  }

  /** Rebuild the flange from the untouched state captured when its dialog opened. */
  previewFlange(widthMm: number): FlangeResult {
    const before = this.flangePreview;
    if (!before) {
      return {
        applied: false,
        addedSamples: 0,
        region: null,
        reason: "Open the Flange preview before changing its width.",
      };
    }

    this.restoreGeometry(before);
    const result = applyFieldFlange(this.field, widthMm);
    if (result.applied) {
      regularizeAll(this.field);
      // A flange deliberately grows beyond the original foam blank.
      relaxSurfaceBand(this.field, result.region, 6, 0.62, true);
    }
    this.settleWholeGeometry();
    return result;
  }

  commitFlangePreview(): boolean {
    const before = this.flangePreview;
    if (!before) return false;
    const after = this.captureGeometry();
    this.flangePreview = null;
    if (fieldsEqual(before.field, after.field)) return false;

    this.history.push({
      label: "Add flange",
      bytes:
        before.field.byteLength +
        before.dirty.byteLength +
        after.field.byteLength +
        after.dirty.byteLength,
      undo: () => this.restoreGeometryAndSettle(before),
      redo: () => this.restoreGeometryAndSettle(after),
    });
    this.emit("history");
    return true;
  }

  cancelFlangePreview(): void {
    const before = this.flangePreview;
    if (!before) return;
    this.flangePreview = null;
    this.restoreGeometryAndSettle(before);
  }

  /** Add one flat-bottomed, ramped ring around the current outer footprint. */
  addFlange(widthMm: number): FlangeResult {
    this.beginFlangePreview();
    const result = this.previewFlange(widthMm);
    if (result.applied) this.commitFlangePreview();
    else this.cancelFlangePreview();
    return result;
  }

  // ------------------------------------------------------ overall smooth

  beginOverallSmoothPreview(): void {
    if (this.flangePreview) this.cancelFlangePreview();
    if (this.overallSmoothPreview) this.cancelOverallSmoothPreview();
    this.overallSmoothPreview = this.captureGeometry();
  }

  /** Rebuild a live preview from the untouched state captured when the dialog opened. */
  previewOverallSmooth(strengthPercent: number): OverallSmoothResult {
    const before = this.overallSmoothPreview;
    if (!before) {
      return {
        applied: false,
        changedSamples: 0,
        reason: "Open Overall Smooth before changing its preview.",
      };
    }

    this.restoreGeometry(before);
    const result = applyOverallSmooth(this.field, strengthPercent);
    if (result.applied) {
      this.fixingList = this.fixingList.filter(
        (fixing) => this.checkFixingPlacement(fixing.x, fixing.z, fixing.typeId).valid,
      );
    }
    this.settleWholeGeometry();
    return result;
  }

  commitOverallSmoothPreview(): boolean {
    const before = this.overallSmoothPreview;
    if (!before) return false;
    const after = this.captureGeometry();
    this.overallSmoothPreview = null;
    if (fieldsEqual(before.field, after.field)) return false;

    this.history.push({
      label: "Overall smooth",
      bytes:
        before.field.byteLength +
        before.dirty.byteLength +
        after.field.byteLength +
        after.dirty.byteLength,
      undo: () => this.restoreGeometryAndSettle(before),
      redo: () => this.restoreGeometryAndSettle(after),
    });
    this.emit("history");
    return true;
  }

  cancelOverallSmoothPreview(): void {
    const before = this.overallSmoothPreview;
    if (!before) return;
    this.overallSmoothPreview = null;
    this.restoreGeometryAndSettle(before);
  }

  smoothAll(strengthPercent: number): OverallSmoothResult {
    this.beginOverallSmoothPreview();
    const result = this.previewOverallSmooth(strengthPercent);
    if (result.applied) this.commitOverallSmoothPreview();
    else this.cancelOverallSmoothPreview();
    return result;
  }

  // ------------------------------------------------ floating geometry cleanup

  removeFloatingGeometry(): RemoveFloatingResult {
    const before = this.captureGeometry();
    const result = removeFloatingFieldGeometry(this.field);
    if (!result.applied) return result;

    this.fixingList = this.fixingList.filter(
      (fixing) => this.checkFixingPlacement(fixing.x, fixing.z, fixing.typeId).valid,
    );
    this.glossList = this.glossList.filter(
      (dab) => this.field.sample(dab.x, dab.y, dab.z) <= this.field.h * 1.5,
    );
    const after = this.captureGeometry();
    this.settleWholeGeometry();
    this.emit("finish");
    this.history.push({
      label: "Remove floating geometry",
      bytes:
        before.field.byteLength +
        before.dirty.byteLength +
        after.field.byteLength +
        after.dirty.byteLength,
      undo: () => this.restoreGeometryAndSettle(before),
      redo: () => this.restoreGeometryAndSettle(after),
    });
    this.emit("history");
    return result;
  }

  private captureGeometry(): GeometrySnapshot {
    return {
      field: new Float32Array(this.field.data),
      dirty: new Uint8Array(this.field.dirty),
      fixings: [...this.fixingList],
      glossDabs: [...this.glossList],
    };
  }

  private restoreGeometry(snapshot: GeometrySnapshot): void {
    this.field.data.set(snapshot.field);
    this.field.dirty.set(snapshot.dirty);
    this.fixingList = [...snapshot.fixings];
    this.glossList = [...snapshot.glossDabs];
  }

  private settleWholeGeometry(): void {
    this.mesher.markAllDirty();
    this.mesher.flush();
    this.invalidateSolid();
    this.emit("geometry");
    this.emit("fixings");
  }

  private restoreGeometryAndSettle(snapshot: GeometrySnapshot): void {
    this.restoreGeometry(snapshot);
    this.settleWholeGeometry();
    this.emit("finish");
  }

  // --------------------------------------------------------------- reset

  reset(): void {
    const beforeField = new Float32Array(this.field.data);
    const beforeDirty = new Uint8Array(this.field.dirty);
    const beforeFixings = this.fixingList;
    const beforeGloss = [...this.glossList];

    const restore = (
      data: Float32Array,
      dirty: Uint8Array,
      fixings: Fixing[],
      glossDabs: GlossDab[],
    ): void => {
      this.field.data.set(data);
      this.field.dirty.set(dirty);
      this.fixingList = fixings;
      this.glossList = [...glossDabs];
      this.mesher.markAllDirty();
      this.mesher.flush();
      this.invalidateSolid();
      this.emit("geometry");
      this.emit("fixings");
      this.emit("finish");
    };

    this.field.resetToBlank();
    this.fixingList = [];
    this.glossList = [];
    this.mesher.markAllDirty();
    this.mesher.flush();
    this.invalidateSolid();
    this.emit("geometry");
    this.emit("fixings");
    this.emit("finish");

    const afterField = new Float32Array(this.field.data);
    const afterDirty = new Uint8Array(this.field.dirty);
    this.history.push({
      label: "Reset block",
      bytes: beforeField.byteLength + afterField.byteLength,
      undo: () => restore(beforeField, beforeDirty, [...beforeFixings], beforeGloss),
      redo: () => restore(afterField, afterDirty, [], []),
    });
    this.emit("history");
  }

  get hasEdits(): boolean {
    if (this.fixingList.length > 0) return true;
    if (this.glossList.length > 0) return true;
    const { dirty } = this.field;
    for (let i = 0; i < dirty.length; i += 7) {
      if ((dirty[i] as number) !== 0) return true;
    }
    return false;
  }

  // --------------------------------------------------------- persistence

  toSaved(): SavedDesign {
    const preview = this.overallSmoothPreview ?? this.flangePreview;
    return {
      version: 1,
      presetId: this.preset.id,
      width: this.preset.width,
      height: this.preset.height,
      depth: this.preset.depth,
      voxelSize: this.preset.voxelSize,
      field: (preview?.field ?? this.field.data).slice().buffer,
      dirty: (preview?.dirty ?? this.field.dirty).slice().buffer,
      fixings: [...(preview?.fixings ?? this.fixingList)],
      glossDabs: [...(preview?.glossDabs ?? this.glossList)],
      savedAt: Date.now(),
    };
  }

  /** Returns false when the record does not fit this engine's grid. */
  restore(saved: SavedDesign): boolean {
    const values = new Float32Array(saved.field);
    const flags = new Uint8Array(saved.dirty);
    if (values.length === this.field.data.length && flags.length === this.field.dirty.length) {
      this.field.data.set(values);
      this.field.dirty.set(flags);
    } else {
      // Saves made before external flanges had only the original two-cell
      // horizontal padding. Centre that old field inside the wider workspace.
      const legacyPad = 2;
      const legacyNx = Math.ceil(saved.width / saved.voxelSize) + legacyPad * 2 + 1;
      const legacyNy = Math.ceil(saved.height / saved.voxelSize) + legacyPad * 2 + 1;
      const legacyNz = Math.ceil(saved.depth / saved.voxelSize) + legacyPad * 2 + 1;
      if (
        values.length !== legacyNx * legacyNy * legacyNz ||
        flags.length !== values.length ||
        legacyNy !== this.field.ny
      ) {
        return false;
      }
      const offsetI = this.field.horizontalPad - legacyPad;
      const offsetK = this.field.horizontalPad - legacyPad;
      this.field.resetToBlank();
      for (let k = 0; k < legacyNz; k++) {
        for (let j = 0; j < legacyNy; j++) {
          const source = legacyNx * (j + legacyNy * k);
          const target = this.field.index(offsetI, j, offsetK + k);
          this.field.data.set(values.subarray(source, source + legacyNx), target);
          this.field.dirty.set(flags.subarray(source, source + legacyNx), target);
        }
      }
    }
    // Older autosaves may contain cut faces made before the automatic polish
    // pass existed. Sign-preserving relaxation upgrades them safely in place.
    relaxSurfaceBand(this.field, null, 3, 0.55);
    this.fixingList = saved.fixings.filter((f) => typeof f.x === "number" && typeof f.z === "number");
    this.glossList = (saved.glossDabs ?? []).filter(
      (dab) =>
        typeof dab.x === "number" &&
        typeof dab.y === "number" &&
        typeof dab.z === "number" &&
        typeof dab.radius === "number",
    );
    this.history.clear();
    this.mesher.markAllDirty();
    this.mesher.flush();
    this.invalidateSolid();
    this.emit("geometry");
    this.emit("fixings");
    this.emit("finish");
    this.emit("history");
    return true;
  }

  dispose(): void {
    this.disposeSolid();
    this.mesher.dispose();
    this.listeners.clear();
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

function fieldsEqual(a: Float32Array, b: Float32Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/** Regularise a region with a margin, so the cut face meshes cleanly. */
function regularizeRegionAround(field: VoxelField, region: SampleRegion): void {
  regularizeRegion(field, expandSampleRegion(field, region, 2));
}

function expandSampleRegion(field: VoxelField, region: SampleRegion, margin: number): SampleRegion {
  return {
    i0: Math.max(0, region.i0 - margin),
    i1: Math.min(field.nx, region.i1 + margin),
    j0: Math.max(0, region.j0 - margin),
    j1: Math.min(field.ny, region.j1 + margin),
    k0: Math.max(0, region.k0 - margin),
    k1: Math.min(field.nz, region.k1 + margin),
  };
}
